import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import { api, resolveImage } from "@/lib/api";
import Reveal, { RevealStagger, revealItem } from "@/components/Reveal";

const CATS = [
  { key: "all", label_en: "All", label_es: "Todo" },
  { key: "carpentry", label_en: "Carpentry", label_es: "Ebanistería" },
  { key: "construction", label_en: "Construction", label_es: "Construcción" },
  { key: "consultation", label_en: "3D / Design", label_es: "3D / Diseño" },
];

export default function Portfolio() {
  const { lang, t, pick } = useLang();
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/projects")
      .then(({ data }) => setProjects(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      active === "all"
        ? projects
        : projects.filter((p) => p.category === active),
    [projects, active]
  );

  return (
    <div data-testid="page-portfolio" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24">
      <section className="pt-20 pb-12">
        <Reveal>
          <p className="text-eyebrow text-brand mb-6">{t("portfolio.eyebrow")}</p>
          <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl text-ink leading-none tracking-tighter">
            {t("portfolio.title")}
          </h1>
        </Reveal>
      </section>

      <div className="flex flex-wrap items-center gap-2 sm:gap-4 border-y border-edge py-5 mb-12">
        {CATS.map((c) => (
          <button
            key={c.key}
            data-testid={`portfolio-filter-${c.key}`}
            onClick={() => setActive(c.key)}
            className={`px-4 py-2 text-eyebrow transition-colors ${
              active === c.key
                ? "text-ink-inverse bg-brand"
                : "text-ink-muted hover:text-brand"
            }`}
          >
            {lang === "en" ? c.label_en : c.label_es}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-ink-muted py-20 text-center">…</div>
      ) : filtered.length === 0 ? (
        <div className="text-ink-muted py-20 text-center" data-testid="portfolio-empty">
          {t("portfolio.empty")}
        </div>
      ) : (
        <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-32" staggerDelay={0.08} key={active}>
          {filtered.map((p) => (
            <motion.article
              key={p.id}
              variants={revealItem}
              data-testid={`portfolio-card-${p.id}`}
              className="group border border-edge bg-bg-surface hover:border-brand transition-colors"
            >
              <div className="relative h-[360px] overflow-hidden">
                <motion.img
                  src={resolveImage(p.cover_image)}
                  alt={pick(p, "title")}
                  className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0"
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-eyebrow text-brand">{p.category}</span>
                  <span className="text-eyebrow text-ink-dim">{p.year}</span>
                </div>
                <h3 className="font-display text-2xl text-ink mb-3 leading-tight">
                  {pick(p, "title")}
                </h3>
                <p className="text-sm text-ink-muted leading-relaxed line-clamp-3">
                  {pick(p, "description")}
                </p>
                {p.location && (
                  <p className="mt-5 text-eyebrow text-ink-dim">{p.location}</p>
                )}
              </div>
            </motion.article>
          ))}
        </RevealStagger>
      )}
    </div>
  );
}
